"use client";

import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import useGetMessageTemplates from "@/hooks/requests/message-template/use-get-message-templates";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import { ICustomerTemplate } from "@/interfaces/customer-template.interface";
import { MessageTemplateMessages } from "@/messages/message-template/message-template.messages";

import MessageTemplateFiltersForm from "./components/FiltersForm";
import { MessageTemplateTable } from "./components/Table";
import { initialFilterValues } from "./constants";

type Props = {
  messages: MessageTemplateMessages;
};

const LOADING_SPINNER_SIZE = 50;
const CARD_MARGIN_TOP = 16;
const FIRST_PAGE = 0;

function MessageTemplatesPage({ messages }: Props) {
  const pagination = usePagination();
  const { pageNumber, pageSize, changePage } = pagination;

  const { filters, changeFilters } = useFilters<ICustomerTemplate>({
    initialValue: initialFilterValues,
  });

  const { data, isLoading, error } = useGetMessageTemplates({
    pageNumber,
    pageSize,
    filter: filters.filter((filter) => filter.value !== ""),
  });

  const shouldLoadTable = !!data && !isLoading && !error && data.items.length > 0;
  const shouldLoadError = !data && !isLoading && !!error;
  const shouldLoadNoFound = !!data && !isLoading && !error && data.items.length === 0;

  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <Formik
          initialValues={filters}
          validateOnChange={false}
          onSubmit={(newFilters) => {
            changePage(FIRST_PAGE);
            changeFilters(newFilters);
          }}
        >
          <MessageTemplateFiltersForm messages={messages} />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(CARD_MARGIN_TOP) })}>
          <Suspense
            isSuspended={isLoading}
            fallback={<CircularProgress size={LOADING_SPINNER_SIZE} />}
          >
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="bold">
                  {messages.tableTitle}
                </MDTypography>
              </MDBox>

              {!!shouldLoadTable && (
                <MessageTemplateTable pagination={pagination} data={data} messages={messages} />
              )}

              {!!shouldLoadError && (
                <AlertBlock
                  title={messages.errorToLoad}
                  description={error.message}
                  alt={messages.notFoundAlt}
                />
              )}

              {!!shouldLoadNoFound && (
                <AlertBlock
                  title={messages.notFoundError}
                  description={messages.notFoundDescription}
                  alt={messages.notFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default MessageTemplatesPage;
